import { useFeatureGate } from '@statsig/react-bindings';
import { GridContainer, GridItem } from '@thangdevalone/meeting-grid-layout-react';
import { useAtomValue } from 'jotai';
import { ReactNode } from 'react';

import { telehealthMeetingGridLayoutGate } from '../../statsig/gates';
import { remoteParticipantsWithMediaMetadataAtom } from '../../video-sdk/core/room/participant/media/atoms';
import { DEFAULT_ASPECT_RATIO } from '../../video-sdk/core/room/participant/media/constants';
import { RemoteParticipantWithMediaMetadata } from '../../video-sdk/core/types';
import { RemoteParticipantWrapperProps } from './RemoteParticipantCell';
import { RemoteParticipantGridLegacyWrapper } from './RemoteParticipantGridLegacy';

/**
 * Falls back to {@link DEFAULT_ASPECT_RATIO} until the participant's video
 * publishes its dimensions.
 */
function getAspectRatio({ mediaMetadata }: RemoteParticipantWithMediaMetadata) {
  return mediaMetadata?.video?.aspectRatio ?? DEFAULT_ASPECT_RATIO;
}

export interface RemoteParticipantGridProps {
  remoteParticipants: RemoteParticipantWithMediaMetadata[];
  remoteParticipantComponent: (props: RemoteParticipantWrapperProps) => ReactNode;
  disableAnimations?: boolean;
}

/**
 * Displays remote participants in a grid layout.
 */
export function RemoteParticipantGrid({
  remoteParticipants,
  remoteParticipantComponent: RemoteParticipantComponent,
  disableAnimations = true,
}: RemoteParticipantGridProps) {
  const aspectRatios = remoteParticipants.map((remoteParticipant) => {
    const { width, height } = getAspectRatio(remoteParticipant);
    return `${width}:${height}`;
  });
  return (
    <GridContainer
      disableAnimation={disableAnimations}
      gap={8}
      layoutMode='gallery'
      disableFloat
      data-testid='remote-audio-video-grid'
      itemAspectRatios={aspectRatios}
    >
      {remoteParticipants.map((remoteParticipantWithMediaMetadata, i) => {
        const { participant: remoteParticipant } = remoteParticipantWithMediaMetadata;
        const { width, height } = getAspectRatio(remoteParticipantWithMediaMetadata);

        return (
          <GridItem index={i} key={remoteParticipant.identity}>
            <RemoteParticipantComponent
              // Move the container and video class selectors into the component
              // after launching the grid gate
              className='h-full'
              remoteParticipant={remoteParticipant}
              // These override the `video-player` class in index.css - remove
              // that class after launch and move these into the component
              videoClassName='object-cover h-full'
              videoStyle={{ aspectRatio: `${width} / ${height}` }}
            />
          </GridItem>
        );
      })}
    </GridContainer>
  );
}

type RemoteParticipantGridWrapperProps = Pick<RemoteParticipantGridProps, 'remoteParticipantComponent'>;

/**
 * Temporary. Inline this component after launching the feature gate.
 */
function RemoteParticipantGridWrapper_({
  remoteParticipantComponent: RemoteParticipantComponent,
}: RemoteParticipantGridWrapperProps) {
  const participants = useAtomValue(remoteParticipantsWithMediaMetadataAtom);

  return (
    <RemoteParticipantGrid remoteParticipants={participants} remoteParticipantComponent={RemoteParticipantComponent} />
  );
}

/**
 * Picks between the smart meeting grid layout and the
 * {@link RemoteParticipantGridLegacy} CSS grid based on the
 * {@link telehealthMeetingGridLayoutGate} feature gate.
 */
export function RemoteParticipantGridWrapper({ remoteParticipantComponent }: RemoteParticipantGridWrapperProps) {
  const { value: isMeetingGridLayoutEnabled } = useFeatureGate(telehealthMeetingGridLayoutGate);

  return isMeetingGridLayoutEnabled ? (
    <RemoteParticipantGridWrapper_ remoteParticipantComponent={remoteParticipantComponent} />
  ) : (
    <RemoteParticipantGridLegacyWrapper remoteParticipantComponent={remoteParticipantComponent} />
  );
}
